import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface BulkActionBarProps {
  /** Number of selected items. The bar is hidden entirely when this is 0. */
  count: number;
  /** Singular noun for the selected item, e.g. "variant" or "product". */
  itemNoun: string;
  /** Action buttons / controls, rendered on the right of the bar. */
  children: ReactNode;
  /** Clears the selection. */
  onClear: () => void;
}

/**
 * Floating bottom action bar — slides up from the bottom of the page whenever
 * one or more rows are selected, and carries the bulk-edit actions. Shared by
 * the Products and Variants lists so bulk editing looks and behaves identically
 * on both. Offset to clear the sidebar on desktop; full-width on mobile.
 */
export function BulkActionBar({ count, itemNoun, children, onClear }: BulkActionBarProps) {
  if (count === 0) return null;

  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-3 z-40 flex justify-center px-3',
        'md:bottom-6 md:pl-[252px] md:pr-6',
      )}
    >
      <div
        className={cn(
          'pointer-events-auto flex w-full max-w-3xl flex-col gap-2.5 rounded-xl border border-border',
          'bg-card/95 p-2.5 shadow-2xl ring-1 ring-black/5 backdrop-blur',
          'animate-in slide-in-from-bottom-4 fade-in duration-200',
          'sm:flex-row sm:items-center sm:gap-3 sm:py-2 sm:pl-3.5 sm:pr-2.5',
        )}
      >
        <div className="flex shrink-0 items-center gap-2">
          <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
            {count}
          </span>
          <span className="text-sm font-medium text-foreground">
            {itemNoun}
            {count === 1 ? '' : 's'} selected
          </span>
          <button
            type="button"
            onClick={onClear}
            className="ml-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="hidden h-6 w-px bg-border sm:block" />
        <div className="flex flex-1 flex-wrap items-center gap-2 sm:justify-end">
          {children}
        </div>
      </div>
    </div>
  );
}
